import { renderedHref } from '../Utils/checkout-path'
import { isSavedAddress } from '../Utils/checkout-address'
import { PaymentMethodType, Contact, SummaryData, ShippingOption, ServerAddressAttributes, Address } from '../types'
import { sleepMs } from 'src/Elements/Utils/general'
import { CheckoutCart } from 'src/Elements/Checkout/V2/types'
import { VariantId, LineItemId, PriceId } from 'src/Elements/types'
import { CF2Element } from 'javascript/lander/runtime'
import { buildCustomFields, checkValidCustomFields, hasPhysicalProducts } from './checkout-utils'
import { decodeVirtualInstallmentId } from './checkout-store'

export type UpdateTransactionData = {
  amount: number
  lineItems: { label: string; amount: string | number }[]
  status?: 'success' | 'fail'
  shippingOptions?: { id: string; label: string; description: string; amount: number }[]
}

export type SubmitPurchaseCartDetailsProductVariants = {
  id: VariantId
  line_item_id?: LineItemId
  price_id: PriceId
  quantity: number
}

type SubmitPurchaseCartDetails = {
  order_id?: number
  product_variants: SubmitPurchaseCartDetailsProductVariants[]
}

type SubmitPayloadBillingAddress = {
  billing_address_attributes?: ServerAddressAttributes
  billing_same_as_shipping?: boolean
}

type SubmitPayloadShippingAddress = {
  shipping_addresses_attributes?: {
    physical_address_attributes: ServerAddressAttributes
  }[]
}

type PaymentMethodDetails = {
  payment_method_id?: number
  payment_method_type?: PaymentMethodType
  rebilly_token?: string
}

type SubmitPayloadPurchase = {
  process_new_order: boolean
  selected_shipping_option?: any
  coupon_codes?: string[]
  installment_duration?: number
  installment_type?: string
} & SubmitPurchaseCartDetails &
  PaymentMethodDetails

type SubmitPayloadContact = Contact & SubmitPayloadShippingAddress

export type SubmitPayload = {
  purchase: SubmitPayloadPurchase
  contact: SubmitPayloadContact
  turnstileToken: string
  redirect_to?: string
  cart_contact_id?: number
  skip_optin_track?: boolean
}

export type OnValidationErrorFn = (error?: any) => boolean

const REBILLY_PAYMENT_METHOD_BY_TYPE_MAP = {
  Klarna: 'klarna-payments',
}
export class CheckoutSubmit {
  static #redirecTo = document.querySelector('[href="#submit-checkout-form"]').getAttribute('data-on-submit-go-to')
  static #threedsListenerEnabled = false
  static #formSubmitPayload = null

  static submitFromButtonClick(
    submitButton: HTMLElement,
    onValidationError: OnValidationErrorFn,
    redirectOverride?: string
  ): Promise<void> | void {
    this.#add3dsListener()
    const checkoutElement = submitButton.closest<CF2Element>('.elCheckout')
    if (
      this.checkSubmitShowErrors({ skipFields: new Set(['payment']) }) &&
      this.checkSubmitShowErrors({ onlyFields: new Set(['payment']) }) &&
      globalThis.Checkout.utils.canSubmit() &&
      checkValidCustomFields(checkoutElement)
    ) {
      globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.SUBMITTING)
      globalThis.Checkout.store.submitting.set({
        state: globalThis.Checkout.SubmittingStates.START,
      })
      const isUpgradeDowngrade =
        globalThis.Checkout.store.checkout.mode.get() == globalThis.Checkout.CheckoutStates.UPGRADE_DOWNGRADE
      const capturedPaymentMethodId = globalThis.Checkout.store.payment.id.get()
      const canSkipSubmitRebilly = isUpgradeDowngrade || capturedPaymentMethodId
      if (canSkipSubmitRebilly) {
        const payload = this.buildPayloadFromStore({
          contact: globalThis.Checkout.store.contact.get(),
          turnstileToken: globalThis.Checkout.turnstileToken,
          shippingAddress: globalThis.Checkout.store.shipping.get(),
          billingAddress: globalThis.Checkout.store.billing.get(),
          paymentMethodDetails: this.#buildPaymentMethodDetails() ?? {},
          selectedShippingOption: globalThis.Checkout.store.shippingOption.get(),
          skipBillingAddress: globalThis.Checkout.utils.skipBillingAddress(globalThis.Checkout.store),
          redirectOverride: redirectOverride,
        })
        return this.submit(payload)
      } else {
        const paymentType = globalThis.Checkout.store.payment.type.get()
        if (['Klarna', 'payment-card'].includes(paymentType)) {
          // NOTE: payment-card always obtain a token calling Rebilly.createToken method
          return this.#submitRebilly(onValidationError).then((tokenData) => {
            // // NOTE: tokenData can be empty if user closes the modal when using klarna
            if (!tokenData && paymentType === 'Klarna') return
            globalThis.Checkout.store.payment[paymentType].token.set(tokenData.id)
            const payload = this.buildPayloadFromStore({
              contact: globalThis.Checkout.store.contact.get(),
              turnstileToken: globalThis.Checkout.turnstileToken,
              shippingAddress: globalThis.Checkout.store.shipping.get(),
              billingAddress: globalThis.Checkout.store.billing.get(),
              paymentMethodDetails: this.#buildPaymentMethodDetails() ?? {},
              selectedShippingOption: globalThis.Checkout.store.shippingOption.get(),
              skipBillingAddress: globalThis.Checkout.utils.skipBillingAddress(globalThis.Checkout.store),
              redirectOverride: redirectOverride,
            })
            return this.submit(payload)
          })
        } else {
          const payload = this.buildPayloadFromStore({
            contact: globalThis.Checkout.store.contact.get(),
            turnstileToken: globalThis.Checkout.turnstileToken,
            shippingAddress: globalThis.Checkout.store.shipping.get(),
            billingAddress: globalThis.Checkout.store.billing.get(),
            paymentMethodDetails: this.#buildPaymentMethodDetails() ?? {},
            selectedShippingOption: globalThis.Checkout.store.shippingOption.get(),
            skipBillingAddress: globalThis.Checkout.utils.skipBillingAddress(globalThis.Checkout.store),
            redirectOverride: redirectOverride,
          })
          return this.submit(payload)
        }
      }
    } else {
      onValidationError()
    }
  }

  static checkSubmitShowErrors(options: { onlyFields?: Set<string>; skipFields?: Set<string> }): boolean {
    const { onlyFields, skipFields } = options ?? {}
    return Object.entries(globalThis.Checkout.computed.errorsByName)
      .map(([name, computed]: [string, any]) => {
        if (onlyFields && !onlyFields.has(name)) return true
        if (skipFields && skipFields.has(name)) return true

        globalThis.Checkout.store.showAllErrors[name].set(true)
        return !globalThis.Checkout.utils.hasErrors(computed.get())
      })
      .every((v) => !!v)
  }

  static buildPayloadFromStore(
    payload: Partial<{
      contact: Contact
      turnstileToken: string
      shippingAddress: ServerAddressAttributes
      billingAddress: ServerAddressAttributes
      paymentMethodDetails: PaymentMethodDetails
      selectedShippingOption: ShippingOption
      skipBillingAddress: boolean
      redirectOverride?: string
    }>
  ): SubmitPayload {
    const addressParams =
      this.#buildAddressParams(
        {
          billingAddress: payload.billingAddress,
          shippingAddress: payload.shippingAddress,
        },
        {
          skipBillingAddress: payload.skipBillingAddress,
        }
      ) ?? {}
    const paymentDetails = payload.paymentMethodDetails
    const purchaseCartDetails = this.buildPurchaseCartDetails()
    // NOTE: Temp fix for FHL 2023
    const customFields = buildCustomFields()
    const contact = payload.contact
    const couponCode = globalThis.Checkout.store.coupons.appliedCode.get()
    const couponData = couponCode ? { coupon_codes: [couponCode] } : {}
    const virtualInstallmentId = globalThis.Checkout.store.payment.virtualInstallmentId.get()
    const installmentData = decodeVirtualInstallmentId(virtualInstallmentId)

    const rebillyPayload: SubmitPayload = {
      // NOTE: Temp fix for FHL 2023
      ...(customFields ? customFields : {}),
      contact: {
        ...contact,
        ...(addressParams.shipping ?? {}),
      },
      turnstileToken: payload.turnstileToken,
      ...(addressParams.billing ?? {}),
      purchase: {
        ...couponData,
        ...purchaseCartDetails,
        ...paymentDetails,
        process_new_order: true,
        ...(paymentDetails?.payment_method_type === 'payment-card' && installmentData
          ? {
              installment_duration: installmentData.duration,
              installment_type: installmentData.type,
            }
          : {}),
      },
      skip_optin_track: Boolean(globalThis.leadsCollected),
    }
    if (this.#shouldIncludeRegistration()) rebillyPayload['registration'] = this.#buildRegistrationDetails()

    const selected_shipping_option = payload.selectedShippingOption
    if (selected_shipping_option && Object.keys(selected_shipping_option).length > 1) {
      rebillyPayload.purchase.selected_shipping_option = selected_shipping_option
    }

    const redirectUrl = payload.redirectOverride || this.#redirecTo
    if (redirectUrl) {
      rebillyPayload.redirect_to = redirectUrl
    }

    return rebillyPayload
  }

  static #leadSourceGenerator(): Record<string, string> {
    const DEFAULT_MAX_CHARS_LENGTH = 512
    const leadQueryParamMapping = {
      utm_source: {
        name: 'source',
      },
      utm_medium: {
        name: 'medium',
      },
      utm_campaign: {
        name: 'campaign',
      },
      utm_term: {
        name: 'term',
      },
      utm_content: {
        name: 'content',
      },
      affiliate: {},
      subAffiliate: {},
      clickId: {},
      salesAgent: {},
    }
    const params = new URLSearchParams(globalThis.location.search)
    return Array.from(params.keys()).reduce((acc, key) => {
      const mappedValue = leadQueryParamMapping[key]
      if (mappedValue) {
        const paramValue = params.get(key)
        const leadSourceName = mappedValue.name ?? key
        acc[leadSourceName] = paramValue.substring(0, DEFAULT_MAX_CHARS_LENGTH)
      }
      return acc
    }, {})
  }

  static #submitRebilly(onValidationError: OnValidationErrorFn): Promise<{ id: string }> {
    const paymentType = globalThis.Checkout.store.payment.type.get()
    const method = REBILLY_PAYMENT_METHOD_BY_TYPE_MAP[paymentType] ?? paymentType
    const Rebilly = globalThis.Rebilly
    let extraData = { method } as any

    const leadSource = this.#leadSourceGenerator()
    if (Object.keys(leadSource).length) {
      extraData = { ...extraData, leadSource }
    }

    const form = document.querySelector('#cfAR')
    const rebillyDataMapping = {
      firstName: 'first_name',
      lastName: 'last_name',
      emails: 'email',
      ...(paymentType === 'Klarna' ? { country: 'country' } : {}),
    }

    const rebillyFieldKeyMapping = {
      'paymentInstrument.cvv': 'cvv',
    }

    Object.entries(rebillyDataMapping).forEach(([rebillyKey, dataKey]) => {
      const input = form.querySelector<HTMLInputElement>(`[data-rebilly="${rebillyKey}"]`)
      if (input) {
        let value = globalThis.Checkout.store.contact.get()[dataKey]
        if (dataKey === 'country') {
          const cartHasPhysicalProducts = hasPhysicalProducts(globalThis.Checkout.computed.checkoutCart.get())
          const billingSameAsShipping = globalThis.Checkout.store.billingSameAsShipping.get()
          const key = cartHasPhysicalProducts && billingSameAsShipping ? 'shipping' : 'billing'
          value = globalThis.Checkout.store[key].get()[dataKey] ?? globalThis.Checkout.contactLocale.country
        }
        input.value = value
      }
    })

    globalThis.clearBlankInputFields()
    return Rebilly.createToken(form, extraData).catch((e) => {
      console.error(e)
      if (e.invalidFields && e.invalidFields.length) {
        e.invalidFields.forEach((error) => {
          const key = rebillyFieldKeyMapping[error.field]
          if (key) {
            const event = { valid: false, error: { message: error.message } }
            globalThis.Checkout.store.payment['payment-card'].events.setKey(key, event)
          }
        })
        globalThis.Checkout.store.submitting.set({
          state: globalThis.Checkout.SubmittingStates.ERROR,
          code: globalThis.Checkout.ErrorTypes.REBILLY_ERROR,
        })
      } else {
        let message = e.message
        if (e.details.length) {
          const details = e.details.join(' - ')
          message = `${message} - ${details}`
        }
        globalThis.Checkout.store.submitting.set({
          state: globalThis.Checkout.SubmittingStates.ERROR,
          code: globalThis.Checkout.ErrorTypes.REBILLY_ERROR,
          message: message,
        })
      }

      onValidationError(e)
    })
  }

  static generateShippingOptionId(shippingOption: ShippingOption): string {
    return [shippingOption.description, shippingOption.amount_formatted].join('$')
  }

  static buildDigitalWalletTransactionData(
    summaryData: SummaryData,
    cart: CheckoutCart,
    shippingEnabled: boolean,
    shippingOptions: ShippingOption[]
  ): UpdateTransactionData {
    const { line_items } = summaryData
    const lineItems = line_items.map(({ price, description }, index) => {
      let label
      if (line_items.length == cart.items.length) {
        const variantId = cart.items[index].variant_id
        const variant = globalThis.Checkout.variantsById[variantId]
        label = variant.name
      } else {
        label = description
      }
      return {
        label,
        amount: price,
      }
    })

    if (summaryData.tax?.amount > 0) {
      lineItems.push({
        label: 'Taxes',
        amount: summaryData.tax.amount,
      })
    }

    if (summaryData.shipping?.amount > 0) {
      lineItems.push({
        label: 'Shipping',
        amount: summaryData.shipping.amount,
      })
    }

    if (summaryData.discount?.amount > 0) {
      lineItems.push({
        label: 'Discount',
        amount: -summaryData.discount?.amount,
      })
    }

    return {
      amount: summaryData.total.amount,
      lineItems,
      ...(shippingEnabled
        ? {
            status: shippingOptions.length ? 'success' : 'fail',
            shippingOptions: shippingOptions.map((shippingOption) => ({
              id: this.generateShippingOptionId(shippingOption),
              label: shippingOption.description,
              description: '',
              amount: Number(shippingOption.amount.amount),
            })),
          }
        : {}),
    }
  }

  static buildPurchaseCartDetails(): SubmitPurchaseCartDetails {
    const checkoutCart = globalThis.Checkout.computed.checkoutCart.get()
    const lineItems: SubmitPurchaseCartDetailsProductVariants[] = checkoutCart.items.map(
      ({ variant_id, price_id, line_item_id, quantity }): SubmitPurchaseCartDetailsProductVariants => {
        return {
          id: variant_id,
          quantity,
          price_id,
          ...(line_item_id ? { line_item_id } : {}),
        }
      }
    )
    return { order_id: checkoutCart.order_id, product_variants: lineItems }
  }

  static #buildPaymentMethodDetails(): PaymentMethodDetails {
    const mode = globalThis.Checkout.store.checkout.mode.get()
    if (mode == globalThis.Checkout.CheckoutStates.UPGRADE_DOWNGRADE) return
    const payment_method_type = globalThis.Checkout.store.payment.type.get()

    const rebilly_token = globalThis.Checkout.store.payment[payment_method_type].token.get()
    const payment_method_id = globalThis.Checkout.store.payment.id.get()

    if (payment_method_id) return { payment_method_id, rebilly_token: null }

    return {
      payment_method_id: null,
      payment_method_type,
      rebilly_token,
    }
  }

  static #buildRegistrationDetails(): { calendar_event_id: string; occurs_at: string } {
    return {
      calendar_event_id: (document.getElementsByName('registration[calendar_event_id]')[0] as HTMLInputElement).value,
      occurs_at: (document.getElementsByName('registration[occurs_at]')[0] as HTMLInputElement).value,
    }
  }

  static #shouldIncludeRegistration(): boolean {
    return document.getElementsByName('registration[calendar_event_id]')?.length > 0
  }

  static submit(payload: SubmitPayload): void {
    if (localStorage.getItem('cf2:devtools:enabled')) {
      console.log('submitted data', payload)
    }

    globalThis.CFDispatchEvent(globalThis.CFEvents.FORM_SUBMITTED, payload)
    let timer = null
    this.#submitOrderAsync(
      payload,
      3,
      () => {
        clearInterval(timer)
        globalThis.Checkout.store.submitting.set({
          state: globalThis.Checkout.SubmittingStates.START,
        })
      },
      (sleepTime) => {
        let remainingSeconds = sleepTime / 1000
        clearInterval(timer)
        timer = setInterval(() => {
          remainingSeconds -= 1
          globalThis.Checkout.store.submitting.set({
            state: globalThis.Checkout.SubmittingStates.WAITING_ON_QUEUE,
            remainingSeconds,
          })
        }, 1000)
      }
    )
      .then((response) => {
        if (response.status == 422) {
          response.json().then((responseJSON) => {
            const errorDetails = responseJSON.error_details
            let errorDetailsMessage = responseJSON.error
            if (responseJSON.error_details && !!errorDetails?.length) {
              errorDetailsMessage = errorDetails.map((e) => e.graceful_message ?? e.message).join('</br>')
              const mode = globalThis.Checkout.store.checkout.mode.get()

              const isRetrieablePayment =
                mode == globalThis.Checkout.CheckoutStates.OTO &&
                errorDetails.find((e) => e.source == 'payment_gateway')
              if (isRetrieablePayment) {
                globalThis.Checkout.store.showAllErrors.payment.set(false)
                globalThis.Checkout.store.payment.id.set(null)
              }
            }
            globalThis.Checkout.store.submitting.set({
              state: globalThis.Checkout.SubmittingStates.ERROR,
              code: globalThis.Checkout.ErrorTypes.SERVER_ERROR_WITH_MESSAGE,
              message: errorDetailsMessage,
            })
          })
        } else if (response.ok) {
          this.#formSubmitPayload = payload
          globalThis.CFDispatchEvent(globalThis.CFEvents.FORM_SUBMITTED_OK, payload)
          if (response.headers.get('X-CF2-APPROVAL-URL')) {
            globalThis.Checkout.store.threeds.set({
              show: true,
              approvalUrl: response.headers.get('X-CF2-APPROVAL-URL'),
            })
          } else {
            globalThis.Checkout.store.submitting.set({
              state: globalThis.Checkout.SubmittingStates.DONE,
            })
            globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.SUBMITTED)
            globalThis.CFDispatchEvent(globalThis.CFEvents.FORM_SUBMITTED_FINALIZED, payload)
            this.#handleFormSubmitRedirect(response, response.headers.get('Location'))
          }
        } else if (response.status >= 300 && response.status < 400) {
          // NOTE: This code is never executed...
          globalThis.Checkout.store.submitting.set({
            state: globalThis.Checkout.SubmittingStates.DONE,
          })
          globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.SUBMITTED)
          globalThis.CFDispatchEvent(globalThis.CFEvents.FORM_SUBMITTED_FINALIZED, payload)
          this.#handleFormSubmitRedirect(response, response.headers.get('Location') ?? globalThis.location.href)
        } else if (response.status === 429) {
          clearInterval(timer)
          globalThis.Checkout.store.submitting.set({
            state: globalThis.Checkout.SubmittingStates.ERROR,
            code: globalThis.Checkout.ErrorTypes.EXCEEDED_MAX_RETRIES,
          })
        } else {
          globalThis.Checkout.store.submitting.set({
            state: globalThis.Checkout.SubmittingStates.ERROR,
            code: globalThis.Checkout.ErrorTypes.UNHANDLED_SERVER_RESPONSE,
          })
        }
      })
      .catch((err) => {
        console.error(err)
        clearInterval(timer)
        globalThis.Checkout.store.submitting.set({
          state: globalThis.Checkout.SubmittingStates.ERROR,
          code: globalThis.Checkout.ErrorTypes.SERVER_ERROR,
        })
      })
  }

  static #buildAddressParams(
    addressParams: { billingAddress?: Address; shippingAddress?: Address },
    options: { skipBillingAddress?: boolean }
  ): {
    billing?: SubmitPayloadBillingAddress
    shipping?: SubmitPayloadShippingAddress
  } {
    const mode = globalThis.Checkout.store.checkout.mode.get()
    const billing = addressParams.billingAddress ?? ({} as Address)
    const shipping = addressParams.shippingAddress ?? ({} as Address)

    if (mode == globalThis.Checkout.CheckoutStates.UPGRADE_DOWNGRADE) {
      return
    }

    let billing_address_attributes: ServerAddressAttributes, physical_address_attributes: ServerAddressAttributes
    if (isSavedAddress(billing)) {
      billing_address_attributes = {
        id: billing.id,
      }
    } else {
      billing_address_attributes = {
        address_one: billing.address,
        address_two: billing.address_2,
        city: billing.city,
        region_name: billing.state,
        country_id: billing.country,
        postal_code: billing.zip,
      }
    }

    if (isSavedAddress(shipping)) {
      physical_address_attributes = {
        id: shipping.id,
      }
    } else {
      physical_address_attributes = {
        address_one: shipping.address,
        address_two: shipping.address_2,
        city: shipping.city,
        region_name: shipping.state,
        country_id: shipping.country,
        postal_code: shipping.zip,
      }
    }

    const checkoutCart = globalThis.Checkout.computed.checkoutCart.get()
    if (hasPhysicalProducts(checkoutCart)) {
      const billingSameAsShipping = globalThis.Checkout.store.billingSameAsShipping.get()
      if (options.skipBillingAddress) {
        return { shipping: { shipping_addresses_attributes: [{ physical_address_attributes }] } }
      }
      if (mode == globalThis.Checkout.CheckoutStates.OTO) {
        return {
          shipping: { shipping_addresses_attributes: [{ physical_address_attributes }] },
          billing: { billing_address_attributes, billing_same_as_shipping: false },
        }
      }
      if (billingSameAsShipping) {
        return {
          shipping: { shipping_addresses_attributes: [{ physical_address_attributes }] },
          billing: { billing_same_as_shipping: true },
        }
      } else {
        return {
          shipping: { shipping_addresses_attributes: [{ physical_address_attributes }] },
          billing: { billing_address_attributes, billing_same_as_shipping: false },
        }
      }
    } else {
      if (options.skipBillingAddress) return
      return { billing: { billing_address_attributes } }
    }
  }

  static async #submitOrderAsync(
    data: SubmitPayload,
    maxRetries = 3,
    onBeforeSubmit: () => void,
    onRetryAfter: (sleepTime: number) => void
  ): Promise<Response> {
    let response
    for (let i = 0; i < maxRetries; i++) {
      onBeforeSubmit()
      response = await fetch(renderedHref(), {
        credentials: 'same-origin',
        method: 'post',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          'X-CF2-POST-TYPE': 'submit',
        },
      }).then((res) => {
        if (res.status >= 500) {
          console.error(res)
          throw Error('500 error')
        }
        return res
      })

      if (response.status === 429) {
        const sleepTime = parseInt(response.headers.get('Retry-After'))
        onRetryAfter(sleepTime)
        console.log(`Waiting on queue, retrying after ${sleepTime}`)
        await sleepMs(sleepTime)
      } else {
        break
      }
    }
    return response
  }

  static async #handleFormSubmitRedirect(response: Response, urlToRedirect: string): Promise<void> {
    globalThis.location.href = urlToRedirect
  }

  static #add3dsListener(): void {
    if (this.#threedsListenerEnabled) return
    this.#threedsListenerEnabled = true

    globalThis.addEventListener('message', (event) => {
      if (event.data.sender == 'CfOrderStatus') {
        const orderDetails = event.data.details

        if (this.#redirecTo) {
          orderDetails.redirect_to = this.#redirecTo
        }

        fetch(renderedHref(), {
          credentials: 'same-origin',
          method: 'post',
          body: JSON.stringify(orderDetails),
          headers: {
            'Content-Type': 'application/json',
            'X-CF2-POST-TYPE': 'submit',
          },
        }).then((response) => {
          if (orderDetails['orderResult'] == 'declined') {
            globalThis.Checkout.store.submitting.set({
              state: globalThis.Checkout.SubmittingStates.ERROR,
              code: globalThis.Checkout.ErrorTypes.THREEDS_DECLINED_ERROR,
            })
          } else {
            if (response.ok) {
              globalThis.Checkout.store.state.set(globalThis.Checkout.StoreStates.SUBMITTED)
              globalThis.CFDispatchEvent(globalThis.CFEvents.FORM_SUBMITTED_FINALIZED, this.#formSubmitPayload)
              this.#handleFormSubmitRedirect(response, response.headers.get('Location'))
            } else {
              response.json().then((r) => {
                globalThis.Checkout.store.submitting.set({
                  state: globalThis.Checkout.SubmittingStates.ERROR,
                  code: globalThis.Checkout.ErrorTypes.THREEDS_DECLINED_CUSTOM_ERROR,
                  message: r.error,
                })
              })
            }
          }
          globalThis.Checkout.store.threeds.set({
            show: false,
            approvalUrl: null,
          })
        })
      }
    })
  }
}
